import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  TextField,
  Button,
  Paper,
  IconButton,
  Stack,
  Divider,
  Alert,
  CircularProgress,
  Tab,
  Tabs,
  Switch,
  FormControlLabel,
  Grid,
} from '@mui/material';
import { Add as AddIcon, ArrowBack as ArrowBackIcon, Delete as DeleteIcon, Save as SaveIcon } from '@mui/icons-material';
import { settingsApi, aiUsageApi } from '../services/api';

const LOCALES = ['en', 'es', 'he'];
const LOCALE_LABELS = { en: 'EN', es: 'ES', he: 'HE' };

const qKey = (i, locale) => `agent.q${i + 1}.${locale}`;

const defaultLocaleData = () => ({ welcome: '', questions: [''] });

const AIAgentConfig = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [activeTab, setActiveTab] = useState(0);

  const [prompt, setPrompt] = useState('');
  const [verifyEnabled, setVerifyEnabled] = useState(false);

  // Numeric behavior settings (stored as strings in the settings table)
  const [behaviorSettings, setBehaviorSettings] = useState({
    'agent.max-rounds':          '4',
    'agent.empty-retries':       '2',
    'agent.repeat-call-limit':   '3',
    'agent.max-output-tokens':   '8192',
    'agent.response-timeout-ms': '30000',
    'agent.context-window-size': '20',
    'agent.daily-token-limit':   '50000',
  });

  const setBehavior = (key, value) =>
    setBehaviorSettings((prev) => ({ ...prev, [key]: value }));

  // Usage management
  const [usageUserId, setUsageUserId] = useState('');
  const [usageResult, setUsageResult] = useState(null); // { totalTokensToday }
  const [usageLoading, setUsageLoading] = useState(false);
  const [usageError, setUsageError] = useState('');
  const [usageSuccess, setUsageSuccess] = useState('');

  const handleCheckUsage = async () => {
    if (!usageUserId.trim()) return;
    setUsageLoading(true); setUsageError(''); setUsageResult(null); setUsageSuccess('');
    try {
      const data = await aiUsageApi.getUsage(usageUserId.trim());
      setUsageResult(data);
    } catch (e) {
      setUsageError(e.message || 'Failed to fetch usage');
    } finally {
      setUsageLoading(false);
    }
  };

  const handleResetUsage = async () => {
    if (!usageUserId.trim()) return;
    setUsageLoading(true); setUsageError(''); setUsageSuccess('');
    try {
      await aiUsageApi.resetUsage(usageUserId.trim());
      setUsageResult(null);
      setUsageSuccess(`Daily token usage reset for user ${usageUserId.trim()}`);
    } catch (e) {
      setUsageError(e.message || 'Failed to reset usage');
    } finally {
      setUsageLoading(false);
    }
  };

  const [localeData, setLocaleData] = useState({
    en: defaultLocaleData(),
    es: defaultLocaleData(),
    he: defaultLocaleData(),
  });
  const [originalQuestionKeys, setOriginalQuestionKeys] = useState({ en: [], es: [], he: [] });

  const activeLocale = LOCALES[activeTab];

  const loadSettings = async () => {
    setLoading(true);
    setError('');
    setSuccess('');
    try {
      const all = await settingsApi.listAll();
      const map = new Map(all.map((s) => [s.key, s]));

      setPrompt(map.get('agent.prompt')?.value || '');
      setVerifyEnabled(map.get('agent.verify.enabled')?.value === 'true');

      setBehaviorSettings((prev) => {
        const next = { ...prev };
        Object.keys(next).forEach((key) => {
          if (map.has(key)) next[key] = map.get(key).value;
        });
        return next;
      });

      const newLocaleData = {};
      const newOriginalKeys = {};
      for (const locale of LOCALES) {
        const qEntries = all
          .filter((s) => new RegExp(`^agent\\.q\\d+\\.${locale}$`).test(s.key))
          .sort((a, b) => {
            const ai = parseInt(a.key.replace(`agent.q`, '').replace(`.${locale}`, ''), 10);
            const bi = parseInt(b.key.replace(`agent.q`, '').replace(`.${locale}`, ''), 10);
            return ai - bi;
          });
        const qs = qEntries.map((e) => e.value);
        newLocaleData[locale] = {
          welcome: map.get(`agent.welcome.${locale}`)?.value || '',
          questions: qs.length ? qs : [''],
        };
        newOriginalKeys[locale] = qEntries.map((e) => e.key);
      }
      setLocaleData(newLocaleData);
      setOriginalQuestionKeys(newOriginalKeys);
    } catch (e) {
      console.error(e);
      setError('Failed to load settings');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadSettings();
  }, []);

  const updateLocale = (locale, patch) => {
    setLocaleData((prev) => ({ ...prev, [locale]: { ...prev[locale], ...patch } }));
  };

  const handleAddQuestion = () => {
    updateLocale(activeLocale, { questions: [...localeData[activeLocale].questions, ''] });
  };

  const handleRemoveQuestion = (index) => {
    updateLocale(activeLocale, {
      questions: localeData[activeLocale].questions.filter((_, i) => i !== index),
    });
  };

  const handleQuestionChange = (index, value) => {
    updateLocale(activeLocale, {
      questions: localeData[activeLocale].questions.map((q, i) => (i === index ? value : q)),
    });
  };

  const handleSave = async () => {
    setSaving(true);
    setError('');
    setSuccess('');
    try {
      await settingsApi.upsert('agent.prompt', prompt ?? '', 'TEXT');
      await settingsApi.upsert('agent.verify.enabled', verifyEnabled ? 'true' : 'false', 'TEXT');

      // Numeric behavior settings
      await Promise.all(
        Object.entries(behaviorSettings).map(([key, val]) =>
          settingsApi.upsert(key, val, 'TEXT')
        )
      );

      const ops = [];
      for (const locale of LOCALES) {
        const { welcome, questions } = localeData[locale];
        ops.push(settingsApi.upsert(`agent.welcome.${locale}`, welcome ?? '', 'TEXT'));

        const trimmed = questions.map((q) => (q ?? '').trim());
        while (trimmed.length > 0 && trimmed[trimmed.length - 1] === '') trimmed.pop();

        trimmed.forEach((q, idx) => {
          ops.push(settingsApi.upsert(qKey(idx, locale), q, 'TEXT'));
        });

        const newKeys = new Set(trimmed.map((_, idx) => qKey(idx, locale)));
        originalQuestionKeys[locale]
          .filter((k) => !newKeys.has(k))
          .forEach((k) => ops.push(settingsApi.delete(k)));
      }

      await Promise.all(ops);
      await loadSettings();
      setSuccess('AI Agent settings saved successfully');
    } catch (e) {
      console.error(e);
      setError('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const current = localeData[activeLocale];

  return (
    <>
      <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/dashboard')} sx={{ mb: 2 }}>
        Dashboard
      </Button>
      <Typography variant="h4" component="h1" gutterBottom>
        AI Agent Configuration
      </Typography>
      <Typography variant="body2" color="text.secondary" gutterBottom>
        Configure the Anipaw AI assistant prompt, welcome message, and sample questions used in the admin and user experiences.
      </Typography>

      {error && (
        <Box mb={2}>
          <Alert severity="error">{error}</Alert>
        </Box>
      )}
      {success && (
        <Box mb={2}>
          <Alert severity="success">{success}</Alert>
        </Box>
      )}

      <Paper sx={{ p: 3 }}>
        {loading ? (
          <Box display="flex" alignItems="center" justifyContent="center" minHeight={200}>
            <CircularProgress />
          </Box>
        ) : (
          <Stack spacing={3}>
            <TextField
              label="Agent System Prompt"
              value={prompt}
              onChange={(e) => setPrompt(e.target.value)}
              fullWidth
              multiline
              minRows={4}
              helperText="High-level system prompt that guides the AI agent's behavior (global — not locale-specific)"
            />

            <Divider />

            <Box>
              <Typography variant="h6" gutterBottom>
                Behavior Settings
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Changes take effect within 60 seconds — no redeploy needed.
                Leave a field as-is to keep the current value.
              </Typography>
              <Grid container spacing={2}>
                {[
                  { key: 'agent.max-rounds',          label: 'Max tool-call rounds',     helper: 'How many tool calls the agent can make per turn before returning a fallback (default 4).' },
                  { key: 'agent.empty-retries',        label: 'Empty output retries',     helper: 'Silent retries on a blank/blocked Gemini response before showing a fallback (default 2).' },
                  { key: 'agent.repeat-call-limit',    label: 'Repeat-call break limit',  helper: 'If the same tool+args fires this many times in one turn, the loop breaks gracefully (default 3).' },
                  { key: 'agent.max-output-tokens',    label: 'Max output tokens',        helper: 'Hard token cap on every Gemini response — circuit breaker against runaway output (default 8192).' },
                  { key: 'agent.response-timeout-ms',  label: 'Response timeout (ms)',    helper: 'Per-call timeout for both blocking and streaming Gemini calls in milliseconds (default 30000).' },
                  { key: 'agent.context-window-size',  label: 'Context window size',      helper: 'Number of recent Q&A messages loaded from DB history per conversation turn (default 20).' },
                  { key: 'agent.daily-token-limit',    label: 'Daily token limit / user', helper: 'Total input + output tokens a user may consume per day before hitting the usage gate (default 50000).' },
                ].map(({ key, label, helper }) => (
                  <Grid item xs={12} sm={6} key={key}>
                    <TextField
                      label={label}
                      value={behaviorSettings[key] ?? ''}
                      onChange={(e) => setBehavior(key, e.target.value)}
                      type="number"
                      inputProps={{ min: 0 }}
                      fullWidth
                      helperText={helper}
                    />
                  </Grid>
                ))}
              </Grid>
            </Box>

            <Divider />

            <Box>
              <Typography variant="h6" gutterBottom>
                Feature Flags
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Changes take effect immediately on the next request — no redeploy needed.
              </Typography>
              <Stack spacing={1}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={verifyEnabled}
                      onChange={(e) => setVerifyEnabled(e.target.checked)}
                      color="primary"
                    />
                  }
                  label={
                    <Box>
                      <Typography variant="body1">Verify Pass</Typography>
                      <Typography variant="caption" color="text.secondary">
                        After a data-bearing answer, runs an independent LLM check to verify the
                        response is grounded in real tool results. On failure, loops once more with
                        the reason injected. Off by default — enable once traffic is stable.
                      </Typography>
                    </Box>
                  }
                />
              </Stack>
            </Box>

            <Divider />

            <Box>
              <Typography variant="h6" gutterBottom>
                Usage Management
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Look up or reset a user's daily AI token consumption. Use the Firebase UID as the user ID.
              </Typography>
              {usageError && <Alert severity="error" sx={{ mb: 1 }}>{usageError}</Alert>}
              {usageSuccess && <Alert severity="success" sx={{ mb: 1 }}>{usageSuccess}</Alert>}
              <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                <TextField
                  label="Firebase User ID"
                  value={usageUserId}
                  onChange={(e) => setUsageUserId(e.target.value)}
                  size="small"
                  sx={{ minWidth: 320 }}
                  placeholder="e.g. abc123XYZ..."
                />
                <Button variant="outlined" onClick={handleCheckUsage} disabled={usageLoading || !usageUserId.trim()}>
                  Check Usage
                </Button>
                <Button variant="outlined" color="warning" onClick={handleResetUsage} disabled={usageLoading || !usageUserId.trim()}>
                  Reset Today
                </Button>
              </Stack>
              {usageResult && (
                <Box mt={1}>
                  <Typography variant="body2">
                    Tokens used today: <strong>{usageResult.totalTokensToday.toLocaleString()}</strong>
                  </Typography>
                </Box>
              )}
            </Box>

            <Divider />

            <Box>
              <Typography variant="h6" gutterBottom>
                Localized Content
              </Typography>
              <Tabs
                value={activeTab}
                onChange={(_, v) => setActiveTab(v)}
                sx={{ mb: 2, borderBottom: 1, borderColor: 'divider' }}
              >
                {LOCALES.map((loc) => (
                  <Tab key={loc} label={LOCALE_LABELS[loc]} />
                ))}
              </Tabs>

              <Stack spacing={3}>
                <TextField
                  label="Agent Welcome Message"
                  value={current.welcome}
                  onChange={(e) => updateLocale(activeLocale, { welcome: e.target.value })}
                  fullWidth
                  multiline
                  minRows={2}
                  helperText="Greeting shown to users when they open the AI chat"
                />

                <Divider />
                <Box>
                  <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
                    <Typography variant="h6">Sample Questions</Typography>
                    <Button startIcon={<AddIcon />} onClick={handleAddQuestion}>
                      Add Question
                    </Button>
                  </Box>

                  <Stack spacing={2}>
                    {current.questions.map((q, idx) => (
                      <Box key={idx} display="flex" gap={1} alignItems="center">
                        <TextField
                          label={`Question ${idx + 1}`}
                          value={q}
                          onChange={(e) => handleQuestionChange(idx, e.target.value)}
                          fullWidth
                        />
                        <IconButton
                          aria-label="remove"
                          onClick={() => handleRemoveQuestion(idx)}
                          disabled={current.questions.length <= 1}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Box>
                    ))}
                  </Stack>
                </Box>
              </Stack>
            </Box>

            <Box display="flex" justifyContent="flex-end" gap={2}>
              <Button variant="outlined" onClick={loadSettings} disabled={saving}>
                Reset
              </Button>
              <Button
                variant="contained"
                startIcon={saving ? <CircularProgress size={18} /> : <SaveIcon />}
                onClick={handleSave}
                disabled={saving}
              >
                {saving ? 'Saving...' : 'Save Changes'}
              </Button>
            </Box>
          </Stack>
        )}
      </Paper>
    </>
  );
};

export default AIAgentConfig;
